/**
 * Layout helpers for React Flow.
 *
 * React Flow doesn't ship a layout engine — it just renders nodes at the
 * positions you give it. Two backends here:
 *
 *   - dagre  — fast, hierarchical, the right default for tab-graphs (most
 *              sessions look like trees with a few cycles). Left-to-right by
 *              default; configurable via `direction`.
 *   - elkjs  — slower but produces nicer dense layouts via the `layered`
 *              algorithm with `org.eclipse.elk.layered.unnecessaryBendpoints`
 *              + better edge routing. Used for the "Force" toggle and as the
 *              default above LARGE_GRAPH_THRESHOLD.
 *
 * Both helpers take React Flow `Node[]` + `Edge[]` (with width/height set on
 * each node so the engine knows the bounding box) and return new nodes with
 * `position` set.
 */

import dagre from "dagre";
import ELK from "elkjs/lib/elk.bundled.js";
import type { Edge, Node } from "@xyflow/react";

/** Node size used when computing layouts. Matches the TabNode component. */
export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 64;

/** Above this node count, the heavier elkjs layout is preferred. */
export const LARGE_GRAPH_THRESHOLD = 500;

/** Above this node count, the canvas automatically engages subtree filtering. */
export const SUBTREE_FILTER_THRESHOLD = 500;

/** Default hop radius for subtree filtering. */
export const DEFAULT_HOP_RADIUS = 2;

export type GraphLayout = "dagre" | "elk";

/** Recommend a layout for a given graph size. */
export function chooseLayout(nodeCount: number): GraphLayout {
  return nodeCount > LARGE_GRAPH_THRESHOLD ? "elk" : "dagre";
}

// ---------------------------------------------------------------------------
// dagre — synchronous, hierarchical
// ---------------------------------------------------------------------------

export function layoutWithDagre(
  nodes: Node[],
  edges: Edge[],
  direction: "LR" | "TB" = "LR",
): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    nodesep: 40,
    ranksep: 80,
    acyclicer: "greedy",
    ranker: "network-simplex",
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of nodes) {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    return {
      ...n,
      // dagre returns the centre of the node; React Flow expects the top-left
      // corner. Subtract half the dimensions to convert.
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
    };
  });
}

// ---------------------------------------------------------------------------
// elkjs — asynchronous, force-directed-ish via the "layered" algorithm
// ---------------------------------------------------------------------------

const elk = new ELK();

export async function layoutWithElk(
  nodes: Node[],
  edges: Edge[],
  direction: "RIGHT" | "DOWN" = "RIGHT",
): Promise<Node[]> {
  const graph = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": direction,
      "elk.layered.spacing.nodeNodeBetweenLayers": "80",
      "elk.spacing.nodeNode": "40",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      "elk.layered.crossingMinimization.semiInteractive": "true",
    },
    children: nodes.map((n) => ({
      id: n.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    })),
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  const laid = await elk.layout(graph);
  const positions = new Map<string, { x: number; y: number }>();
  for (const c of laid.children ?? []) {
    positions.set(c.id, { x: c.x ?? 0, y: c.y ?? 0 });
  }

  return nodes.map((n) => ({
    ...n,
    position: positions.get(n.id) ?? { x: 0, y: 0 },
  }));
}

// ---------------------------------------------------------------------------
// Subtree filtering
// ---------------------------------------------------------------------------

/**
 * Collect all node IDs within `hops` hops of `focusId`. Works on both
 * directed and undirected adjacency — useful for "show what I clicked
 * plus its neighbourhood" in large graphs.
 */
export function collectSubtreeIds(
  edges: Edge[],
  focusId: string,
  hops: number,
): Set<string> {
  // Build an undirected adjacency list.
  const adj = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a)!.add(b);
  };
  for (const e of edges) {
    add(e.source, e.target);
    add(e.target, e.source);
  }

  const visited = new Set<string>([focusId]);
  let frontier: string[] = [focusId];
  for (let h = 0; h < hops; h++) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const nb of adj.get(id) ?? []) {
        if (!visited.has(nb)) {
          visited.add(nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
    if (frontier.length === 0) break;
  }
  return visited;
}
